import React, { useState } from 'react';
import { Users, Lightbulb, Sparkles } from 'lucide-react';

const CustomerValueScore = ({ customerEmail, cartValue, platform }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputEmail, setInputEmail] = useState(customerEmail || '');
  const [inputCartValue, setInputCartValue] = useState(cartValue || '');

  const getInsights = async () => {
    if (!inputEmail && !inputCartValue) {
      setError('Please enter customer email or cart value');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
          ? 'http://localhost:3003' 
          : 'https://cartresq-yiq2.onrender.com');
      
      const response = await fetch(`${apiBaseUrl}/api/ai/customer-value`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customerEmail: inputEmail || undefined,
          cartValue: inputCartValue ? parseFloat(inputCartValue) : undefined,
          platform
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Customer insights error:', err);
      setError(err.message || 'Failed to get customer insights');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-5 h-5 text-purple-600" />
        <h3 className="text-sm font-medium text-gray-900">AI Customer Insights</h3>
      </div>

      <div className="space-y-2 mb-3">
        <div>
          <label className="block text-xs text-gray-700 mb-1">Customer Email (optional)</label>
          <input
            type="email"
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="customer@example.com"
            value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
            disabled={!!customerEmail}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-700 mb-1">Cart Value (optional)</label>
          <input
            type="number"
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="0.00"
            value={inputCartValue}
            onChange={(e) => setInputCartValue(e.target.value)}
            disabled={!!cartValue}
          />
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 mb-2 p-2 bg-red-50 rounded">
          {error}
        </div>
      )}

      <button
        onClick={getInsights}
        disabled={loading || (!inputEmail && !inputCartValue)}
        className="w-full text-xs px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Analyzing...' : 'Get Insights'}
      </button>

      {result && (
        <div className="mt-4 border border-purple-200 rounded-lg overflow-hidden bg-purple-50">
          {/* Customer Insight */}
          <div className="p-4">
            <div className="flex items-start gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <span className="text-xs font-semibold text-purple-900">Customer Insight</span>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed">
              "{result.insight}"
            </p>
          </div>

          {/* Hint */}
          {result.hint && (
            <div className="px-4 py-3 bg-purple-100 border-t border-purple-200">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-medium text-purple-800">Hint:</span>
                <span className="text-xs text-purple-900">{result.hint}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerValueScore;
